'use client'

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { AppointmentStatus, AppointmentWithDetails } from '@garage/shared'
import { APPOINTMENT_STATUS_LABELS } from '@garage/shared'
import { Button, StatusDot, useSnackbar } from '@/ui'
import { api } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'
import { formatAppointmentTime, appointmentJobcardUrl } from '@/lib/appointments.js'
import { AppointmentDialog } from '@/components/AppointmentDialog.js'

interface TodaysAppointmentsProps {
  appointments: AppointmentWithDetails[]
  todayDate: string
  onRefresh: () => void
}

const APPOINTMENT_TONE: Record<AppointmentStatus, 'violet' | 'success' | 'error' | 'neutral'> = {
  scheduled: 'violet',
  arrived: 'success',
  no_show: 'error',
  cancelled: 'neutral',
}

export function TodaysAppointments({ appointments, todayDate, onRefresh }: TodaysAppointmentsProps) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()

  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false)
  const [editingAppointment, setEditingAppointment] = useState<AppointmentWithDetails | undefined>()

  const setStatusMutation = useMutation({
    mutationFn: (input: { appointmentId: string; status: AppointmentStatus }) =>
      api.appointments.setStatus(input.appointmentId, { status: input.status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: qk.dashboard })
      onRefresh()
    },
    onError: (error) => {
      snackbar.error(error instanceof Error ? error.message : t('common.errorOccurred'))
    },
  })

  const handleOpenDialog = (appointment?: AppointmentWithDetails) => {
    setEditingAppointment(appointment)
    setAppointmentDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setAppointmentDialogOpen(false)
    setEditingAppointment(undefined)
  }

  const handleRefresh = () => {
    onRefresh()
  }

  // Format the date in the current language
  const dateFormatter = new Intl.DateTimeFormat(i18n.language === 'ar' ? 'ar-u-nu-latn' : 'en-US', {
    month: 'short',
    day: 'numeric',
  })
  const [y, m, d] = todayDate.split('-').map(Number)
  const formattedDate = dateFormatter.format(new Date(y!, m! - 1, d!))

  return (
    <>
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="text-title-md text-on-surface">
              {t('dashboard.todaysAppointments.title')} · {appointments.length}
            </h2>
            <p className="text-label-sm text-on-surface-variant">{formattedDate}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/calendar"
              className="text-label-lg text-on-surface-variant hover:text-on-surface transition-colors"
            >
              {t('dashboard.todaysAppointments.openCalendar')}
            </Link>
            <Button
              variant="outlined"
              size="sm"
              onClick={() => handleOpenDialog()}
            >
              {t('dashboard.todaysAppointments.addAppointment')}
            </Button>
          </div>
        </div>

        {appointments.length === 0 ? (
          <div className="rounded-lg border border-outline-variant bg-surface-high p-4 text-center">
            <p className="text-body-sm text-on-surface-variant">
              {t('dashboard.todaysAppointments.none')}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {appointments.map((appointment) => (
              <div
                key={appointment.id}
                className="rounded-md border border-outline-variant bg-surface-high p-3 space-y-2"
              >
                {/* Header: time · customer · phone · car */}
                <div className="text-body-sm text-on-surface space-y-1">
                  <div className="flex flex-wrap gap-1 min-w-0">
                    <span className="flex-shrink-0 font-medium">
                      {formatAppointmentTime(appointment.time) || t('appointments.anyTime')}
                    </span>
                    {appointment.customerLabel && (
                      <>
                        <span className="flex-shrink-0">·</span>
                        <span className="break-words min-w-0">{appointment.customerLabel}</span>
                      </>
                    )}
                  </div>

                  {appointment.phoneLabel && (
                    <div>
                      <a
                        href={`tel:${appointment.phoneLabel}`}
                        className="text-primary interactive"
                        dir="ltr"
                      >
                        {appointment.phoneLabel}
                      </a>
                    </div>
                  )}

                  {appointment.carLabel && (
                    <div className="text-on-surface">{appointment.carLabel}</div>
                  )}

                  {appointment.reason && (
                    <div className="text-on-surface-variant">{appointment.reason}</div>
                  )}
                </div>

                {/* Status badge */}
                <div className="flex items-center gap-2">
                  <StatusDot
                    tone={APPOINTMENT_TONE[appointment.status]}
                    label={t(`appointments.status.${appointment.status}`, APPOINTMENT_STATUS_LABELS[appointment.status])}
                  />
                </div>

                {/* Actions row */}
                <div className="flex gap-2 flex-wrap pt-1">
                  {appointment.status === 'scheduled' && (
                    <>
                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'arrived' })}
                        disabled={setStatusMutation.isPending}
                      >
                        {t('appointments.actions.arrived')}
                      </Button>
                      <Button
                        variant="filled"
                        size="sm"
                        onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'no_show' })}
                        disabled={setStatusMutation.isPending}
                      >
                        {t('appointments.actions.didntCome')}
                      </Button>
                    </>
                  )}
                  {appointment.status !== 'scheduled' && (
                    <Button
                      variant="filled"
                      size="sm"
                      onClick={() => setStatusMutation.mutate({ appointmentId: appointment.id, status: 'scheduled' })}
                      disabled={setStatusMutation.isPending}
                    >
                      {t('appointments.actions.backToExpected')}
                    </Button>
                  )}

                  <Button
                    variant="filled"
                    size="sm"
                    onClick={() => handleOpenDialog(appointment)}
                  >
                    {t('common.edit')}
                  </Button>

                  {!appointment.jobcardId && appointment.status !== 'cancelled' && (
                    <Button
                      variant="filled"
                      size="sm"
                      onClick={() => navigate(appointmentJobcardUrl(appointment))}
                    >
                      {t('appointments.createJobcard')}
                    </Button>
                  )}

                  {appointment.jobcardId && (
                    <Link
                      to={`/jobcards/${appointment.jobcardId}`}
                      className="inline-flex items-center px-3 py-1.5 rounded text-label-lg bg-surface-high border border-outline text-primary hover:bg-surface interactive"
                    >
                      {appointment.jobcardNumber}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {appointmentDialogOpen && (
        <AppointmentDialog
          open={appointmentDialogOpen}
          date={todayDate}
          appointment={editingAppointment}
          onClose={handleCloseDialog}
          onSaved={handleRefresh}
        />
      )}
    </>
  )
}
